import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  CONSTANT,
  LoaderService,
  ObservableService,
  OrderService,
} from '@bst-frontend/shared';
import { Subscription } from 'rxjs';

@Component({
  selector: 'bst-frontend-add-estimate-modal',
  templateUrl: './add-estimate-modal.component.html',
  styleUrls: ['./add-estimate-modal.component.css'],
})
export class AddEstimateModalComponent {
  @Input() orderData: any;
  weightKg = 0;
  @Output() saved = new EventEmitter<any>();
  private subscriptions: Subscription[] = [];

  form!: FormGroup;
  constructor(
    private activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private orderService: OrderService,
    private loader: LoaderService,
    private observableService: ObservableService
  ) {}

  ngOnInit(): void {
    this.weightKg = this.orderData.totals.totalWeight;
    this.buildForm();
    this.populateFromOrderData();
    this.subscriptions.push(
      this.form.valueChanges.subscribe(() => this.recalculateTotals())
    );
    this.form.valueChanges.subscribe(() => this.recalculateTotals());
    // subscribe to each extra charge's changes
    this.subscribeToExtraCharges();
    // initial calc
    this.recalculateTotals();
  }

  private buildForm() {
    this.form = this.fb.group({
      perKgRate: [2, [Validators.required, Validators.min(0)]], // default example
      tempoCharge: [0, [Validators.min(0)]],
      hamali: [0, [Validators.min(0)]],
      holdingCharge: [100, [Validators.min(0)]],
      extraCharges: this.fb.array([]),
    });
  }

  private populateFromOrderData() {
    if (!this.orderData) return;
    const existingEstimate = this.orderData?.estimate ?? null;
    if (existingEstimate != null) {
      this.form.patchValue({ perKgRate: Number(existingEstimate) });
    }
    if (this.orderData?.totals?.totalWeight != null) {
      this.weightKg =
        Number(this.orderData.totals.totalWeight) || this.weightKg;
    }
    while (this.extraCharges.length) {
      this.extraCharges.removeAt(0);
    }

    const otherCharges = Array.isArray(this.orderData?.otherCharges)
      ? this.orderData.otherCharges
      : [];

    otherCharges.forEach((ch: any) => {
      const label = String(ch.label || '').trim();
      const chargeVal = Number(ch.charge) || 0;
      const id = ch._id || null;

      if (label === 'Tempo charge') {
        this.form.patchValue({ tempoCharge: chargeVal });
      } else if (label === 'Hamali') {
        this.form.patchValue({ hamali: chargeVal });
      } else if (label === 'Holding Charge' || label === 'L R charge') {
        this.form.patchValue({ holdingCharge: chargeVal });
      } else {
        this.extraCharges.push(this.buildExtraCharge(label, chargeVal, id));
      }
    });
  }

  buildExtraCharge(name = '', amount = 0, id?: string) {
    return this.fb.group({
      _id: [id || null],
      name: [name, Validators.required],
      amount: [amount, [Validators.required, Validators.min(0)]],
    });
  }

  get extraCharges(): FormArray {
    return this.form.get('extraCharges') as FormArray;
  }

  addExtraCharge() {
    this.extraCharges.push(this.buildExtraCharge());
    // keep calculate running via valueChanges subscription
  }

  removeExtraCharge(index: number) {
    this.extraCharges.removeAt(index);
    this.recalculateTotals();
  }

  // computed values for template convenience
  get estimateAmount(): number {
    const rate = Number(this.form.get('perKgRate')?.value) || 0;
    return this.weightKg * rate;
  }

  recalculateTotals() {
    // This function just ensures totals derived are available; you can expand validations here.
    const tempo1 = Number(this.form.get('tempoCharge')?.value) || 0;
    const tempo2 = Number(this.form.get('hamali')?.value) || 0;
    const lr = Number(this.form.get('holdingCharge')?.value) || 0;
    const extras = this.extraCharges.controls
      .map((c) => Number(c.get('amount')?.value) || 0)
      .reduce((a, b) => a + b, 0);

    this._orderTotal = this.estimateAmount + tempo1 + tempo2 + lr + extras;
  }

  private _orderTotal = 0;
  get orderTotal(): number {
    return this._orderTotal;
  }

  controlInvalid(control: AbstractControl | null): boolean {
    return !!(control && control.invalid && (control.touched || control.dirty));
  }

  getExtraControl(idx: number, name: 'name' | 'amount'): AbstractControl {
    return (this.extraCharges.at(idx) as FormGroup).get(
      name
    ) as AbstractControl;
  }

  trackByIndex(index: number): number {
    return index;
  }

  async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // Build "otherCharges" exactly as required
    const otherCharges = [];

    // 1. Tempo charges
    if (this.form.value.tempoCharge > 0) {
      otherCharges.push({
        label: 'Tempo charge',
        charge: Number(this.form.value.tempoCharge),
      });
    }
    if (this.form.value.hamali > 0) {
      otherCharges.push({
        label: 'Hamali',
        charge: Number(this.form.value.hamali),
      });
    }

    // 2. Holding Charge
    if (this.form.value.holdingCharge > 0) {
      otherCharges.push({
        label: 'Holding Charge',
        charge: Number(this.form.value.holdingCharge),
      });
    }

    // 3. Extra Charges (dynamic rows)
    this.extraCharges.controls.forEach((ctrl) => {
      const name = ctrl.get('name')?.value;
      const amount = Number(ctrl.get('amount')?.value || 0);
      if (name && amount > 0) {
        otherCharges.push({
          label: name,
          charge: amount,
        });
      }
    });

    // Final payload:
    const payload = {
      estimate: this.form.get('perKgRate')?.value,
      otherCharges,
    };
    this.loader.show();
    try {
      await this.orderService
        .updateOrderStatus(
          CONSTANT.orderStatus['ESTIMATE'],
          this.orderData._id,
          payload
        )
        .toPromise();
      this.observableService.send({ refresh: true });
      // this.loader.hide();
    } catch (error) {
      console.log('Error', error);
    }

    // this.saved.emit(payload);
    this.activeModal.close({ status: 'saved' });
  }

  cancel() {
    this.activeModal.dismiss('cancel');
  }

  private subscribeToExtraCharges() {
    this.subscriptions = this.subscriptions.filter((s) => {
      s.unsubscribe();
      return false;
    });

    // subscribe to array-level valueChanges (keeps things simple)
    const arrSub = this.extraCharges.valueChanges.subscribe(() =>
      this.recalculateTotals()
    );
    this.subscriptions.push(arrSub);

    // also subscribe individually so that anytime a control becomes dirty/touched we can react (optional)
    for (let i = 0; i < this.extraCharges.length; i++) {
      const grp = this.extraCharges.at(i);
      const s = grp.valueChanges.subscribe(() => this.recalculateTotals());
      this.subscriptions.push(s);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
  }
}
